import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { toAbsoluteUrl, useDataProvider } from 'src/app/common/utils';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import { NotePaginateList, NoteResultListParam } from '../../../types/note-setting';
import { getDayEnd, getDayStart } from 'src/app/common/utils';
import NoteSettingList from './components/NoteSettingList';
import { fetchNoteResultList } from '../../../network/noteCrud';
import { makeStyles } from 'tss-react/mui';

type NoteListingPageProps = {} & ParamsProps;

const initialState: NoteResultListParam = {
  formName: '',
  updatedBy: '',
  updatedDateFrom: null,
  updatedDateTo: null,
  page: 1,
  limit: 5,
};

export const noteFilterKeys = ['formName', 'updatedBy', 'updatedDateFrom', 'updatedDateTo'];

const useStyles = makeStyles()((theme) => ({
  rowContainer: {
    display: 'flex',
    alignItems: 'start',
    gap: 25,
  },
  rowSpacing: {
    marginBottom: 16,
  },
  upperCaseText: {
    textTransform: 'uppercase',
  },
  textWeight: {
    fontWeight: 700,
  },
  lineMargin: {
    marginBottom: 5,
  },
  linkStyle: {
    color: 'blue',
    textDecoration: 'underline',
  },
}));

const paramsInitiator = (initialParams?: Record<string, string>): NoteResultListParam => {
  return initialParams
    ? {
        formName: initialParams.formName || '',
        updatedBy: initialParams.updatedBy || '',
        updatedDateFrom: initialParams.updatedDateFrom ? getDayStart(new Date(initialParams.updatedDateFrom)) : null,
        updatedDateTo: initialParams.updatedDateTo ? getDayEnd(new Date(initialParams.updatedDateTo)) : null,
        page: 1,
        limit: 5,
      }
    : initialState;
};

const NoteSettingListingPage: FC<NoteListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState<NoteResultListParam>(paramsInitiator(initialParams));
  const [sortKey, setSortKey] = useState<{ key: string; value?: string }[]>([]);
  const [noteList, setNoteList] = useState<NotePaginateList>();

  const updateSortingKey = (sortingKey: { key: string; value?: string }) => {
    const sortingKeyArray = sortKey.filter((currentValue, index, arr) => {
      return currentValue.key !== sortingKey.key;
    });
    sortingKeyArray.unshift(sortingKey);
    setSortKey(sortingKeyArray);
    refreshData();
  };

  const { isLoading, refreshData } = useDataProvider<NotePaginateList>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchNoteResultList(filterState, sortKey, dispatch);
      } catch (err) {}
    },
    setNoteList,
    false,
  );

  return (
    <>
      <PruFilter
        title={Translation('noteSetting.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'formName',
            initialValue: filterState.formName,
            displayName: Translation('noteSetting.formName'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'updatedBy',
            initialValue: filterState.updatedBy,
            displayName: Translation('noteSetting.lastUpdatedBy'),
            choices: [
              { displayName: '', value: '' },
              ...(noteList?.filter?.updatedBy
                ?.filter((item) => item?.toString()?.length)
                ?.map?.((item) => {
                  return {
                    displayName: item,
                    value: item,
                  };
                }) || []),
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'updatedDateFrom',
            fieldTo: 'updatedDateTo',
            initialDateFrom: filterState.updatedDateFrom,
            initialDateTo: filterState.updatedDateTo,
            displayName: Translation('noteSetting.lastUpdatedAt'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            formName: data.formName,
            updatedBy: data.updatedBy,
            updatedDateFrom: data.updatedDateFrom,
            updatedDateTo: data.updatedDateTo,
          });
        }}
        fetchData={refreshData}
      />

      <div className={`${classes.rowContainer} ${classes.rowSpacing}`}>
        <div>
          <p className={`${classes.upperCaseText} ${classes.textWeight}`}>
            {Translation('noteSetting.download.referTitle')}:
          </p>
        </div>
        <div>
          <ul>
            <li className={`${classes.upperCaseText} ${classes.lineMargin}`}>
              <a
                className={classes.linkStyle}
                href={toAbsoluteUrl('/templates/Hoach_dinh_thu_nhap_TVV_CSTN_2026.xlsx')}
              >
                {Translation('noteSetting.download.agFile')}
              </a>
            </li>
            <li className={`${classes.upperCaseText} ${classes.lineMargin}`}>
              <a
                className={classes.linkStyle}
                href={toAbsoluteUrl('/templates/Hoach_dinh_thu_nhap_QLTVV_CSTN_2026.xlsx')}
              >
                {Translation('noteSetting.download.moaFile')}
              </a>
            </li>
            <li className={classes.upperCaseText}>
              <a
                className={classes.linkStyle}
                href={toAbsoluteUrl('/templates/Hoach_dinh_thu_nhap_TDL_CSTN_2026.xlsx')}
              >
                {Translation('noteSetting.download.gadFile')}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <NoteSettingList
        isLoading={isLoading}
        onRefresh={refreshData}
        noteList={noteList}
        onSort={(dataKey) => updateSortingKey(dataKey)}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default NoteSettingListingPage;
